import { memo, useCallback, useEffect, useState } from 'react';
import { Button, Modal, notification } from 'antd';
import BlacklistFormPopup from '../../blacklist/BlacklistFormPopup/FormPopup';
import { Props } from './types';
import styles from './style.module.css';
import { useBlacklistPopup } from '../../../hooks/features/useBlacklistPopup/useBlacklistPopup';

const PHONE_NUMBER_REGEXP = /^\d{10}$/;
const PHONE_VALIDATION_MESSAGE =
  'Введите 10 цифр телефонного номера без 8- и +7';

export type ErrorState = {
  phone?: ErrorData;
  reason?: ErrorData;
};

export type ErrorData = {
  validateStatus: '' | 'error' | 'success' | 'warning' | 'validating';
  help: string;
};

const BlacklistPopup = memo(({ onCancel, open, onSuccess }: Props) => {
  const reportData = useBlacklistPopup();
  const [errors, setErrors] = useState<ErrorState>({});
  const [submitting, setSubmitting] = useState(false);
  const [api, contextHolder] = notification.useNotification();

  useEffect(() => {
    if (open) {
      reportData.handleReset();
      setErrors({});
    }
  }, [open]);

  const checkValid = useCallback(() => {
    let hasError = false;

    const newErrors = {} as ErrorState;

    if (!reportData.phone || !PHONE_NUMBER_REGEXP.test(reportData.phone)) {
      newErrors.phone = {
        validateStatus: 'error',
        help: PHONE_VALIDATION_MESSAGE,
      };

      hasError = true;
    }

    if (!reportData.selectedReason) {
      newErrors.reason = {
        validateStatus: 'error',
        help: 'Укажите причину',
      };

      hasError = true;
    }

    setErrors(newErrors);

    return !hasError;
  }, [reportData.phone, reportData.selectedReason]);

  const handleFocus = (field: 'phone' | 'reason') => {
    const newErrors = { ...errors };
    delete newErrors[field];

    setErrors(newErrors);
  };

  const clearAndClose = useCallback(() => {
    reportData.handleReset();
    setErrors({});
    onCancel();
  }, [reportData, onCancel]);

  const handleMainButton = useCallback(async () => {
    if (submitting) return;
    if (!checkValid()) return;

    setSubmitting(true);
    let shouldClose = false;

    try {
      const result = await reportData.handleOk();
      if (result.type === 'Blacklist/addBlacklistNumber/rejected') {
        api.open({
          message: 'Не удалось заблокировать номер!',
        });
        return;
      }

      onSuccess?.();
      shouldClose = true;
    } catch {
      api.open({
        message: 'Не удалось заблокировать номер!',
      });
    } finally {
      setSubmitting(false);
    }

    if (shouldClose) {
      clearAndClose();
    }
  }, [api, checkValid, clearAndClose, onSuccess, reportData, submitting]);

  return (
    <>
      {contextHolder}
      <Modal
        className={styles.modalContent}
        open={open}
        onCancel={submitting ? undefined : clearAndClose}
        closable={!submitting}
        maskClosable={!submitting}
        destroyOnHidden={true}
        footer={[
          <Button
            key="toForm"
            type="primary"
            onClick={handleMainButton}
            loading={submitting}
            disabled={submitting}
          >
            Добавить номер
          </Button>,
        ]}
      >
        <BlacklistFormPopup
          phone={reportData.phone}
          onPhoneChange={reportData.onPhoneChange}
          selectedReason={reportData.selectedReason}
          onSelectedReasonChange={reportData.onSelectedReasonChange}
          comment={reportData.comment}
          onCommentChange={reportData.onCommentChange}
          errors={errors}
          handleFocus={handleFocus}
          disabled={submitting}
        />
      </Modal>
    </>
  );
});

BlacklistPopup.displayName = 'BlacklistPopup';
export default BlacklistPopup;
